<template>
  <div :class="$style.wrapper">
    <p :class="$style.title">人気のカテゴリー</p>
    <ul v-if="links.length > 0" :class="$style.categories">
      <li v-for="link in links" :key="link.link">
        <div :class="$style.categoryImage">
          <a :href="link.link" @click="onClick(link)">
            <img :src="link.picture.url" alt="" width="100" height="100" />
          </a>
        </div>
        <p :class="$style.categoryText">{{ link.short_title || link.title }}</p>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { createClient } from 'microcms-js-sdk';
import { ref, onMounted } from 'vue';
import * as expirebleLocalStorage from '@/js/lib/helper/expirable-local-storage';

const props = defineProps<{
  apiKey: string;
}>();

interface Link {
  title: string;
  short_title?: string;
  link: string;
  picture: {
    url: string;
  };
}
const links = ref<Link[]>([]);

const microCms = createClient({
  serviceDomain: 'iichi',
  apiKey: props.apiKey,
});

const filters = 'category_id.id[not_exists][and]is_vintage[equals]false[and]query[not_exists]';
const keyForCache = `featured-category-links:${filters}`;
const expirationSeconds = 3600; // 1 hour

onMounted(async () => {
  try {
    const cachedData = expirebleLocalStorage.getFromStorage<string>({ key: keyForCache });
    if (cachedData) {
      links.value = JSON.parse(cachedData) as Link[];
      return;
    }

    const response = await microCms.getList<Link>({
      endpoint: 'featured-category-links',
      queries: { filters },
    });

    if (response.contents.length > 0) {
      links.value = response.contents;

      expirebleLocalStorage.setToStorage({
        key: keyForCache,
        value: JSON.stringify(links.value),
        expirationSeconds,
      });
    }
  } catch (error) {
    window.Sentry?.captureException(error);
  }
});

const onClick = (link: Link) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'click_header_featured_category',
      uri_path: link.link,
      category_name: link.title,
    });
  }
};
</script>

<style module lang="scss">
.wrapper {
  border-bottom: 1px solid var(--border-color);
  padding: 12px 16px;
}

.title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 16px;
  text-align: center;
}

.categories {
  display: flex;
  gap: 24px;
  overflow-x: auto;
  padding-bottom: 12px;

  @media (--sp) {
    gap: 12px;
    margin: 0 -10px;
    padding-bottom: 0;
    padding-left: 10px;
    width: auto;
  }

  > li {
    background-color: var(--color-white);
    display: block;
    font-size: 15px;
    min-width: 20%;
    position: relative;
    width: 20%;

    @media (--sp) {
      min-width: 24%;
      width: 24%;
    }

    &:last-child::after {
      content: '';
      height: 1px;
      left: 100%;
      position: absolute;
      top: 0;
      width: 10px;
    }

    .categoryImage {
      background-color: var(--color-gray200);
      border-radius: 10px;
      position: relative;

      img {
        border-radius: 10px;
        display: block;
        height: auto;
        width: 100%;
      }
    }

    .categoryText {
      padding: 12px 8px;
      text-align: center;

      @media (--sp) {
        font-size: 12px;
        padding: 8px 0;
      }
    }
  }
}
</style>
